import React, { useEffect, useState } from "react";
import { LayoutGrid, Building2, Users, CalendarDays, Megaphone, Copy, ExternalLink, Loader2, Save, Check, ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Tag, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl, setActingBusiness } from "@/lib/apiClient";
import AdminShell from "@/components/AdminShell";
import SalonAdmin from "@/pages/SalonAdmin";
import { StaffManager, Matrix } from "@/pages/SuperAdmin";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageField from "@/components/admin/ImageField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "services", label: "Services", icon: Tag },
  { key: "consent", label: "Consent Forms", icon: FileText },
  { key: "recruit", label: "Recruitment Page", icon: Megaphone },
  { key: "salons", label: "Host Salons", icon: Building2 },
  { key: "staff", label: "Technicians & Availability", icon: Users },
  { key: "matrix", label: "Bookings Matrix", icon: CalendarDays },
];

export default function BusinessAdmin({ actingBusiness = null, onExit = null }) {
  setActingBusiness(actingBusiness ? actingBusiness.id : null);
  const [tab, setTab] = useState("overview");
  const [manageTenant, setManageTenant] = useState(null);
  const [biz, setBiz] = useState(null);
  useEffect(() => { api.get("/admin/business").then((r) => setBiz(r.data)).catch(() => {}); }, [actingBusiness, tab]);
  if (manageTenant) return <SalonAdmin actingTenant={manageTenant} onExit={() => setManageTenant(null)} />;

  return (
    <div>
      {actingBusiness && (
        <div className="bg-[#2C3531] text-[#FDFBF7] px-6 py-2.5 flex items-center justify-between text-sm">
          <span>Managing <strong>{biz?.name || actingBusiness.name}</strong> as platform admin</span>
          <button onClick={onExit} data-testid="exit-business" className="inline-flex items-center gap-1.5 hover:underline"><ArrowLeft className="w-4 h-4" /> Exit to platform</button>
        </div>
      )}
      <AdminShell title={biz?.name || "Business"} subtitle={biz ? `Recruitment page: /partner/${biz.slug}` : ""} tabs={TABS} active={tab} onTab={setTab}>
        {tab === "overview" && <Overview biz={biz} />}
        {tab === "services" && <ServicesManager />}
        {tab === "consent" && <ConsentFormsManager />}
        {tab === "recruit" && <RecruitEditor biz={biz} onSaved={setBiz} />}
        {tab === "salons" && <HostSalons onManage={setManageTenant} />}
        {tab === "staff" && <StaffManager />}
        {tab === "matrix" && <Matrix />}
      </AdminShell>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <Card className="p-5 border-[#E8E6E1] shadow-none">
    <p className="uppercase tracking-[0.15em] text-[10px] font-bold text-[#787672]">{label}</p>
    <p className="font-serif text-3xl font-bold mt-2">{value}</p>
  </Card>
);

function Overview({ biz }) {
  const [salons, setSalons] = useState([]);
  const [staff, setStaff] = useState([]);
  const [apps, setApps] = useState([]);
  useEffect(() => {
    api.get("/admin/business/salons").then((r) => setSalons(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/salon-applications").then((r) => setApps(r.data)).catch(() => {});
  }, []);
  const pending = apps.filter((a) => a.status === "pending").length;
  return (
    <div className="space-y-6" data-testid="business-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Host salons" value={salons.length} />
        <Stat label="Technicians" value={staff.length} />
        <Stat label="Pending applications" value={pending} />
        <Stat label="Total bookings" value={salons.reduce((n, s) => n + (s.booking_count || 0), 0)} />
      </div>
      <Card className="p-6 border-[#E8E6E1] shadow-none">
        <h3 className="font-serif text-lg font-bold mb-2">Grow your host network</h3>
        <p className="text-sm text-[#787672]">Share your recruitment page with local salons, gyms and spas so they can apply to host your pop-up treatment days. Head to the <strong>Recruitment Page</strong> tab to customise it and grab your shareable link & QR code.</p>
      </Card>
    </div>
  );
}

function ListEditor({ label, items, onChange, fields, empty, addLabel = "Add", testid }) {
  const list = items || [];
  const set = (i, k, v) => { const a = list.slice(); a[i] = { ...a[i], [k]: v }; onChange(a); };
  const add = () => onChange([...list, { ...empty }]);
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= list.length) return; const a = list.slice(); [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div className="space-y-3" data-testid={testid}>
      {list.map((it, i) => (
        <Card key={i} className="p-4 border-[#E8E6E1] shadow-none bg-[#FDFBF7] space-y-2.5" data-testid={`${testid}-item-${i}`}>
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold uppercase tracking-wide text-[#9A8C73]">{label} {i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded hover:bg-[#F0EFEB] disabled:opacity-30 flex items-center justify-center"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="w-7 h-7 rounded hover:bg-[#F0EFEB] disabled:opacity-30 flex items-center justify-center"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="w-7 h-7 rounded hover:bg-[#993333]/10 text-[#993333] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          {fields.map((fld) => (
            <div key={fld.key}>
              {fld.label && <p className="text-xs text-[#787672] mb-1">{fld.label}</p>}
              {fld.type === "textarea"
                ? <Textarea rows={2} value={it[fld.key] ?? ""} placeholder={fld.placeholder || fld.label} data-testid={`${testid}-${i}-${fld.key}`} onChange={(e) => set(i, fld.key, e.target.value)} />
                : <Input type={fld.type === "number" ? "number" : "text"} value={it[fld.key] ?? ""} placeholder={fld.placeholder || fld.label} data-testid={`${testid}-${i}-${fld.key}`} onChange={(e) => set(i, fld.key, fld.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />}
            </div>
          ))}
        </Card>
      ))}
      <Button type="button" variant="outline" size="sm" className="rounded-full" data-testid={`${testid}-add`} onClick={add}><Plus className="w-4 h-4 mr-1" /> {addLabel}</Button>
    </div>
  );
}

const emptyService = { name: "", description: "", base_price: "", deposit_amount: 25, duration_min: 60, active: true, consent_form_id: "" };

function ServicesManager() {
  const [list, setList] = useState([]);
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState(emptyService);
  const [editing, setEditing] = useState(null); // id or null
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/services").then((r) => setList(r.data));
  useEffect(() => { load(); api.get("/admin/consent-forms").then((r) => setForms(r.data)).catch(() => {}); }, []);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const reset = () => { setForm(emptyService); setEditing(null); };
  const formName = (id) => forms.find((f) => f.id === id)?.title;
  const submit = async () => {
    if (!form.name || form.base_price === "" || form.base_price === null) return toast.error("Name and base price are required");
    setSaving(true);
    const body = { name: form.name, description: form.description || "", base_price: Number(form.base_price),
      deposit_amount: Number(form.deposit_amount) || 0, duration_min: Number(form.duration_min) || 0, active: form.active !== false,
      consent_form_id: form.consent_form_id || null };
    try {
      if (editing) await api.put(`/admin/services/${editing}`, body);
      else await api.post("/admin/services", body);
      toast.success(editing ? "Service updated" : "Service added"); reset(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const edit = (s) => { setEditing(s.id); setForm({ name: s.name, description: s.description || "", base_price: s.base_price, deposit_amount: s.deposit_amount ?? 25, duration_min: s.duration_min ?? 60, active: s.active !== false, consent_form_id: s.consent_form_id || "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { try { await api.delete(`/admin/services/${id}`); toast.success("Service removed"); if (editing === id) reset(); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleActive = async (s) => { try { await api.put(`/admin/services/${s.id}`, { name: s.name, description: s.description || "", base_price: s.base_price, deposit_amount: s.deposit_amount, duration_min: s.duration_min, active: !(s.active !== false), consent_form_id: s.consent_form_id || null }); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="max-w-4xl grid lg:grid-cols-[minmax(0,1fr)_340px] gap-8" data-testid="services-manager">
      <div className="space-y-3">
        <div><h3 className="font-serif text-xl font-bold">Your services</h3>
          <p className="text-sm text-[#787672]">Add the treatments you offer and the base price you charge. Host salons then add their own markup and choose which to take bookings on.</p></div>
        {list.length === 0 && <p className="text-[#787672] py-6" data-testid="services-empty">No services yet. Add your first service using the form.</p>}
        {list.map((s) => (
          <Card key={s.id} data-testid={`svc-row-${s.id}`} className={`p-4 border-[#E8E6E1] shadow-none flex items-start justify-between gap-4 ${s.active !== false ? "" : "opacity-60"}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-serif text-lg font-bold">{s.name}</h4>
                {s.active === false && <span className="text-[10px] uppercase font-bold text-[#9A8C73] border border-[#E8E6E1] rounded-full px-2 py-0.5">Inactive</span>}
              </div>
              {s.description && <p className="text-sm text-[#787672] mt-0.5">{s.description}</p>}
              <p className="text-sm text-[#3B5341] font-semibold mt-1">£{s.base_price} base · £{s.deposit_amount} deposit{s.duration_min ? ` · ${s.duration_min} min` : ""}</p>
              <p className="text-xs mt-1 inline-flex items-center gap-1 text-[#787672]"><FileText className="w-3 h-3" /> {s.consent_form_id && formName(s.consent_form_id) ? `Consent: ${formName(s.consent_form_id)}` : "No consent form (default terms)"}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch data-testid={`svc-active-${s.id}`} checked={s.active !== false} onCheckedChange={() => toggleActive(s)} />
              <button type="button" data-testid={`svc-edit-${s.id}`} onClick={() => edit(s)} className="w-8 h-8 rounded hover:bg-[#F0EFEB] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
              <button type="button" data-testid={`svc-delete-${s.id}`} onClick={() => remove(s.id)} className="w-8 h-8 rounded hover:bg-[#993333]/10 text-[#993333] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
      <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3 h-fit lg:sticky lg:top-24" data-testid="service-form">
        <h3 className="font-serif text-lg font-bold">{editing ? "Edit service" : "Add a service"}</h3>
        <div><Label>Name</Label><Input data-testid="svc-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="e.g. Cosmetic Teeth Whitening" /></div>
        <div><Label>Description</Label><Textarea rows={2} data-testid="svc-desc" value={form.description} onChange={(e) => upd("description", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Base price (£)</Label><Input type="number" data-testid="svc-base" value={form.base_price} onChange={(e) => upd("base_price", e.target.value)} /></div>
          <div><Label>Deposit (£)</Label><Input type="number" data-testid="svc-deposit" value={form.deposit_amount} onChange={(e) => upd("deposit_amount", e.target.value)} /></div>
        </div>
        <div><Label>Duration (mins)</Label><Input type="number" data-testid="svc-duration" value={form.duration_min} onChange={(e) => upd("duration_min", e.target.value)} /></div>
        <div>
          <Label>Consent form</Label>
          <select data-testid="svc-consent-form" value={form.consent_form_id || ""} onChange={(e) => upd("consent_form_id", e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
            <option value="">No consent form (default terms)</option>
            {forms.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
          </select>
          <p className="text-[11px] text-[#9A8C73] mt-1">Manage forms in the <span className="font-semibold">Consent Forms</span> tab.</p>
        </div>
        <div className="flex items-center gap-2"><Switch data-testid="svc-form-active" checked={form.active !== false} onCheckedChange={(v) => upd("active", v)} /><Label className="!mb-0">Available for booking</Label></div>
        <div className="flex gap-2 pt-1">
          <Button data-testid="svc-save" onClick={submit} disabled={saving} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] flex-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : <><Plus className="w-4 h-4 mr-1" /> Add service</>}</Button>
          {editing && <Button variant="outline" className="rounded-full" onClick={reset}>Cancel</Button>}
        </div>
      </Card>
    </div>
  );
}

const emptyConsent = { title: "Customer Consent Statement", intro: "", statements: [] };

function ConsentFormsManager() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyConsent);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/consent-forms").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const reset = () => { setForm(emptyConsent); setEditing(null); };
  const setStatement = (i, v) => setForm((p) => { const a = (p.statements || []).slice(); a[i] = v; return { ...p, statements: a }; });
  const addStatement = () => setForm((p) => ({ ...p, statements: [...(p.statements || []), ""] }));
  const removeStatement = (i) => setForm((p) => ({ ...p, statements: (p.statements || []).filter((_, j) => j !== i) }));
  const submit = async () => {
    if (!form.title.trim()) return toast.error("A form title is required");
    const statements = (form.statements || []).map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/consent-forms/${editing}`, { title: form.title, intro: form.intro || "", statements });
      else await api.post("/admin/consent-forms", { title: form.title, intro: form.intro || "", statements });
      toast.success(editing ? "Consent form updated" : "Consent form created"); reset(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const edit = (f) => { setEditing(f.id); setForm({ title: f.title, intro: f.intro || "", statements: (f.statements || []).slice() }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { try { await api.delete(`/admin/consent-forms/${id}`); toast.success("Consent form deleted"); if (editing === id) reset(); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="max-w-4xl grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8" data-testid="consent-forms-manager">
      <div className="space-y-3">
        <div><h3 className="font-serif text-xl font-bold">Customer consent forms</h3>
          <p className="text-sm text-[#787672]">Build reusable consent statements. Add each question or statement the client must read, then attach a form to any service in the <span className="font-semibold">Services</span> tab. On the booking page clients tick a single box to accept the whole statement.</p></div>
        {list.length === 0 && <p className="text-[#787672] py-6" data-testid="consent-empty">No consent forms yet. Create your first one using the form.</p>}
        {list.map((f) => (
          <Card key={f.id} data-testid={`consent-row-${f.id}`} className="p-4 border-[#E8E6E1] shadow-none flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h4 className="font-serif text-lg font-bold">{f.title}</h4>
              {f.intro && <p className="text-sm text-[#787672] mt-0.5 line-clamp-2">{f.intro}</p>}
              <p className="text-xs text-[#3B5341] font-semibold mt-1">{(f.statements || []).length} statement{(f.statements || []).length === 1 ? "" : "s"}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button type="button" data-testid={`consent-edit-${f.id}`} onClick={() => edit(f)} className="w-8 h-8 rounded hover:bg-[#F0EFEB] flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
              <button type="button" data-testid={`consent-delete-${f.id}`} onClick={() => remove(f.id)} className="w-8 h-8 rounded hover:bg-[#993333]/10 text-[#993333] flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
      <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3 h-fit lg:sticky lg:top-24" data-testid="consent-form-editor">
        <h3 className="font-serif text-lg font-bold">{editing ? "Edit consent form" : "New consent form"}</h3>
        <div><Label>Form title</Label><Input data-testid="consent-title" value={form.title} onChange={(e) => upd("title", e.target.value)} placeholder="Customer Consent Statement" /></div>
        <div><Label>Intro / legal text (optional)</Label><Textarea rows={3} data-testid="consent-intro" value={form.intro} onChange={(e) => upd("intro", e.target.value)} placeholder="Add any preamble, GDPR or data-retention notice shown above the statements." /></div>
        <div>
          <Label>Consent questions / statements</Label>
          <div className="space-y-2 mt-1" data-testid="consent-statements">
            {(form.statements || []).map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                <Textarea rows={2} data-testid={`consent-statement-${i}`} value={s} onChange={(e) => setStatement(i, e.target.value)} placeholder={`Statement ${i + 1}`} />
                <button type="button" data-testid={`consent-statement-remove-${i}`} onClick={() => removeStatement(i)} className="mt-1 w-8 h-8 rounded hover:bg-[#993333]/10 text-[#993333] flex items-center justify-center shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" className="rounded-full mt-2" data-testid="consent-add-statement" onClick={addStatement}><Plus className="w-4 h-4 mr-1" /> Add question</Button>
        </div>
        <p className="text-[11px] text-[#9A8C73]">Clients see all statements and tick one box: “I accept and agree to the {form.title || "Customer Consent Statement"}”.</p>
        <div className="flex gap-2 pt-1">
          <Button data-testid="consent-save" onClick={submit} disabled={saving} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] flex-1">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : <><Plus className="w-4 h-4 mr-1" /> Create form</>}</Button>
          {editing && <Button variant="outline" className="rounded-full" onClick={reset}>Cancel</Button>}
        </div>
      </Card>
    </div>
  );
}

function RecruitEditor({ biz, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (biz) setF({ ...biz }); }, [biz]);
  if (!f) return <p className="text-[#787672]">Loading…</p>;
  const upd = (k, v) => setF({ ...f, [k]: v });
  const pageUrl = `${window.location.origin}/partner/${f.slug}`;
  const save = async () => {
    setSaving(true);
    try {
      const body = { name: f.name, logo_url: f.logo_url, logo_width: f.logo_width ?? "", logo_height: f.logo_height ?? "",
        primary_color: f.primary_color, secondary_color: f.secondary_color,
        hero_image: f.hero_image, headline: f.headline, pitch: f.pitch,
        intro_title: f.intro_title, intro_body: f.intro_body, video_url: f.video_url || "",
        steps: f.steps || [], case_studies: f.case_studies || [],
        benefits: f.benefits || [], faqs: f.faqs || [], footer_note: f.footer_note,
        contact_email: f.contact_email, contact_phone: f.contact_phone, custom_domain: f.custom_domain || "" };
      const { data } = await api.put("/admin/business", body);
      onSaved(data); toast.success("Recruitment page updated");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const copy = () => { navigator.clipboard.writeText(pageUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500); };
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(pageUrl)}`;

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start" data-testid="recruit-editor">
      <div className="max-w-2xl space-y-5">
        <div className="flex justify-end gap-2">
          <a href={pageUrl} target="_blank" rel="noreferrer" data-testid="recruit-preview" title="Opens the saved page in a new tab"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#E8E6E1] text-sm font-semibold hover:bg-[#F0EFEB] transition-colors">
            <ExternalLink className="w-4 h-4" /> Preview page
          </a>
          <Button data-testid="recruit-save" onClick={save} disabled={saving} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
          </Button>
        </div>
        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-4">
          <h3 className="font-serif text-lg font-bold">Branding</h3>
          <div><Label>Business name</Label><Input data-testid="recruit-name" value={f.name || ""} onChange={(e) => upd("name", e.target.value)} /></div>
          <ImageField label="Logo" value={f.logo_url} onChange={(v) => upd("logo_url", v)} testid="recruit-logo" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Logo width (px)</Label><Input type="number" data-testid="recruit-logo-width" value={f.logo_width ?? ""} onChange={(e) => upd("logo_width", e.target.value === "" ? "" : Number(e.target.value))} placeholder="auto" /></div>
            <div><Label>Logo height (px)</Label><Input type="number" data-testid="recruit-logo-height" value={f.logo_height ?? ""} onChange={(e) => upd("logo_height", e.target.value === "" ? "" : Number(e.target.value))} placeholder="40" /></div>
          </div>
          <p className="text-xs text-[#9A8C73]">Leave width blank to keep the logo's natural ratio. Height controls how large the logo appears in the page header.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Primary colour</Label><div className="flex gap-2"><input type="color" value={f.primary_color || "#2C3531"} onChange={(e) => upd("primary_color", e.target.value)} className="h-10 w-12 rounded" /><Input value={f.primary_color || ""} onChange={(e) => upd("primary_color", e.target.value)} /></div></div>
            <div><Label>Accent colour</Label><div className="flex gap-2"><input type="color" value={f.secondary_color || "#9A8C73"} onChange={(e) => upd("secondary_color", e.target.value)} className="h-10 w-12 rounded" /><Input value={f.secondary_color || ""} onChange={(e) => upd("secondary_color", e.target.value)} /></div></div>
          </div>
        </Card>
        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-4">
          <h3 className="font-serif text-lg font-bold">Page content</h3>
          <div><Label>Headline</Label><Input data-testid="recruit-headline" value={f.headline || ""} onChange={(e) => upd("headline", e.target.value)} /></div>
          <div><Label>Pitch</Label><RichTextEditor value={f.pitch} onChange={(v) => upd("pitch", v)} testid="recruit-pitch" /></div>
          <ImageField label="Hero image" value={f.hero_image} onChange={(v) => upd("hero_image", v)} testid="recruit-hero" />
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-4">
          <h3 className="font-serif text-lg font-bold">Why partner (description)</h3>
          <div><Label>Section title</Label><Input data-testid="recruit-intro-title" value={f.intro_title || ""} onChange={(e) => upd("intro_title", e.target.value)} /></div>
          <div><Label>Description</Label><RichTextEditor value={f.intro_body} onChange={(v) => upd("intro_body", v)} testid="recruit-intro-body" /></div>
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-2">
          <h3 className="font-serif text-lg font-bold">Services & pricing</h3>
          <p className="text-sm text-[#787672]">Your bookable services are managed in the <span className="font-semibold">Services</span> tab. They appear here on your live recruitment page automatically.</p>
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">How hosting works</h3>
          <div><Label>Intro video URL (optional)</Label>
            <Input data-testid="recruit-video" value={f.video_url || ""} onChange={(e) => upd("video_url", e.target.value)} placeholder="YouTube or Vimeo link" />
            <p className="text-xs text-[#9A8C73] mt-1">Shown as an embedded player above the steps.</p></div>
          <ListEditor label="Step" testid="recruit-steps" items={f.steps} onChange={(v) => upd("steps", v)} addLabel="Add step"
            empty={{ num: "", title: "", desc: "" }}
            fields={[
              { key: "num", label: "Number (e.g. 01)" },
              { key: "title", label: "Title" },
              { key: "desc", label: "Description", type: "textarea" },
            ]} />
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">Case studies (earning examples)</h3>
          <ListEditor label="Case study" testid="recruit-cases" items={f.case_studies} onChange={(v) => upd("case_studies", v)} addLabel="Add case study"
            empty={{ title: "", scenario: "", income: "" }}
            fields={[
              { key: "title", label: "Title (e.g. Busy Nail Bar)" },
              { key: "scenario", label: "Scenario", type: "textarea" },
              { key: "income", label: "Potential income (e.g. ~£400 / month extra)" },
            ]} />
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">Benefits</h3>
          <ListEditor label="Benefit" testid="recruit-benefits" items={f.benefits} onChange={(v) => upd("benefits", v)} addLabel="Add benefit"
            empty={{ title: "", desc: "" }}
            fields={[
              { key: "title", label: "Title" },
              { key: "desc", label: "Description", type: "textarea" },
            ]} />
        </Card>

        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-3">
          <h3 className="font-serif text-lg font-bold">FAQ</h3>
          <ListEditor label="Question" testid="recruit-faqs" items={f.faqs} onChange={(v) => upd("faqs", v)} addLabel="Add question"
            empty={{ q: "", a: "" }}
            fields={[
              { key: "q", label: "Question" },
              { key: "a", label: "Answer", type: "textarea" },
            ]} />
        </Card>
        <Card className="p-6 border-[#E8E6E1] shadow-none space-y-4">
          <h3 className="font-serif text-lg font-bold">Contact & footer</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact email</Label><Input data-testid="recruit-email" value={f.contact_email || ""} onChange={(e) => upd("contact_email", e.target.value)} /></div>
            <div><Label>Contact phone</Label><Input value={f.contact_phone || ""} onChange={(e) => upd("contact_phone", e.target.value)} /></div>
          </div>
          <div><Label>Footer tagline</Label><Input data-testid="recruit-footer-note" value={f.footer_note || ""} onChange={(e) => upd("footer_note", e.target.value)} /></div>
          <div>
            <Label>Custom domain (optional)</Label>
            <Input data-testid="recruit-domain" value={f.custom_domain || ""} onChange={(e) => upd("custom_domain", e.target.value)} placeholder="partners.yoursite.com" />
            <p className="text-xs text-[#9A8C73] mt-1">Point this domain's DNS to us and it will map to your recruitment page automatically. Until then, use the path link on the right.</p>
          </div>
        </Card>
      </div>

      <Card className="p-6 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1] sticky top-24" data-testid="share-panel">
        <h3 className="font-serif text-lg font-bold mb-1">Share your page</h3>
        <p className="text-xs text-[#787672] mb-4">Send this to prospective host salons.</p>
        <div className="bg-white border border-[#E8E6E1] rounded-lg px-3 py-2 text-xs break-all mb-2" data-testid="share-link">{pageUrl}</div>
        <div className="flex gap-2 mb-5">
          <Button size="sm" onClick={copy} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] flex-1" data-testid="copy-link">{copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Copy</Button>
          <a href={pageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center px-3 rounded-full border border-[#E8E6E1] text-sm"><ExternalLink className="w-4 h-4" /></a>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-lg p-3 flex justify-center">
          <img src={qr} alt="QR code" className="w-[160px] h-[160px]" data-testid="share-qr" />
        </div>
        <p className="text-xs text-[#787672] text-center mt-2">Print this QR for in-salon posters</p>
      </Card>
    </div>
  );
}

function HostSalons({ onManage }) {
  const [salons, setSalons] = useState([]);
  const [apps, setApps] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ name: "", slug: "", admin_email: "", admin_password: "", deposit_amount: "25", total_price: "99" });
  const [creating, setCreating] = useState(false);
  const loadSalons = () => api.get("/admin/business/salons").then((r) => setSalons(r.data)).catch(() => setSalons([]));
  const loadApps = () => api.get("/admin/salon-applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  useEffect(() => { loadSalons(); loadApps(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/salon-applications/${id}/${action}`); toast.success(`Application ${action}d`); loadSalons(); loadApps(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const createSalon = async () => {
    if (!nf.name || !nf.slug) return toast.error("Name and link are required");
    setCreating(true);
    try {
      await api.post("/tenants", {
        name: nf.name, slug: nf.slug, admin_email: nf.admin_email || undefined,
        admin_password: nf.admin_password || undefined, admin_name: nf.name,
        deposit_amount: parseFloat(nf.deposit_amount) || 0, total_price: parseFloat(nf.total_price) || 0,
      });
      toast.success("Salon created"); setShowNew(false);
      setNf({ name: "", slug: "", admin_email: "", admin_password: "", deposit_amount: "25", total_price: "99" });
      loadSalons();
    } catch (e) { toast.error(apiErr(e)); } finally { setCreating(false); }
  };
  const pending = apps.filter((a) => a.status === "pending");
  return (
    <div className="space-y-8" data-testid="host-salons">
      <div className="flex justify-end">
        <Button data-testid="new-salon-btn" onClick={() => setShowNew(true)} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]"><Plus className="w-4 h-4 mr-1.5" /> New salon</Button>
      </div>
      {pending.length > 0 && (
        <div>
          <h3 className="font-serif text-xl font-bold mb-3">Host applications</h3>
          <div className="max-w-3xl space-y-3">
            {pending.map((a) => (
              <Card key={a.id} className="p-5 border-[#9A8C73]/40 shadow-none bg-[#F7F5F1]" data-testid={`salon-app-${a.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <h4 className="font-serif text-lg font-bold">{a.salon_name}</h4>
                    <p className="text-sm text-[#787672]">{a.contact_name} · {a.email} · {a.phone}</p>
                    <p className="text-sm text-[#787672]">{a.city} · {a.business_type}</p>
                    {a.message && <p className="text-sm mt-2 italic">"{a.message}"</p>}
                  </div>
                  <Badge>{a.status}</Badge>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button data-testid={`approve-salon-${a.id}`} onClick={() => act(a.id, "approve")} className="rounded-full bg-[#3B5341] hover:bg-[#2e4133] flex-1"><Check className="w-4 h-4 mr-1" /> Approve & onboard salon</Button>
                  <Button data-testid={`reject-salon-${a.id}`} onClick={() => act(a.id, "reject")} variant="outline" className="rounded-full flex-1">Reject</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      <div>
        <h3 className="font-serif text-xl font-bold mb-3">Your host salons</h3>
        {salons.length === 0 && <p className="text-[#787672]">No host salons yet. Share your recruitment page to bring them on board.</p>}
        <div className="grid md:grid-cols-2 gap-4">
          {salons.map((t) => (
            <Card key={t.id} className="p-5 border-[#E8E6E1] shadow-none" data-testid={`host-salon-${t.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-serif text-lg font-bold">{t.name}</p>
                  <p className="text-sm text-[#787672]">/book/{t.slug} · {t.booking_count} bookings</p>
                </div>
                <Badge variant={t.status === "active" ? "default" : "secondary"}>{t.status}</Badge>
              </div>
              <Button data-testid={`manage-salon-${t.id}`} onClick={() => onManage(t)} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422] w-full mt-4">Manage salon</Button>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent data-testid="new-salon-dialog">
          <DialogHeader><DialogTitle className="font-serif">Add a host salon</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Salon name</Label><Input data-testid="new-salon-name" value={nf.name} onChange={(e) => { const v = e.target.value; setNf({ ...nf, name: v, slug: nf.slug || slugify(v) }); }} /></div>
            <div><Label>Booking link (slug)</Label><Input data-testid="new-salon-slug" value={nf.slug} onChange={(e) => setNf({ ...nf, slug: slugify(e.target.value) })} placeholder="salon-royale" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin email (optional)</Label><Input data-testid="new-salon-email" value={nf.admin_email} onChange={(e) => setNf({ ...nf, admin_email: e.target.value })} /></div>
              <div><Label>Admin password (optional)</Label><Input data-testid="new-salon-password" type="text" value={nf.admin_password} onChange={(e) => setNf({ ...nf, admin_password: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Deposit (£)</Label><Input type="number" value={nf.deposit_amount} onChange={(e) => setNf({ ...nf, deposit_amount: e.target.value })} /></div>
              <div><Label>Treatment price (£)</Label><Input type="number" value={nf.total_price} onChange={(e) => setNf({ ...nf, total_price: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button data-testid="create-salon-submit" onClick={createSalon} disabled={creating} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create salon"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
